import { AGENT_EVENTS, CLIENT_EVENTS } from "./agentContract";
import buildScenario from "./mockScenario";

/**
 * In-memory implementation of the AgentTransport contract that replays the
 * scripted prospecting scenario (see mockScenario.js). Simulates token
 * streaming so the UI is built against the exact event shapes the future
 * SSE transport will deliver.
 *
 * Engine model: ordered steps, each { id, expects(clientEvent, ctx), run }.
 * The first unconsumed step whose `expects` matches handles the event; a step
 * may return `false` from run() to stay available (e.g. OAuth retry).
 */

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

// Thrown from inside a running turn once `interrupt()` is called; unwinds
// the current step without surfacing as an agent_error (a turn_end for the
// interrupted turn is already emitted at the throw site).
class MockInterrupted extends Error {}

class MockAgentTransport {
  constructor() {
    this.onEvent = null;
    this.disconnected = false;
    this.ts = 0;
    this.turnSeq = 0;
    this.blockSeq = 0;
    this.consumed = {};
    this.ctx = { salesforceConnected: false };
    this.steps = buildScenario();
    this.interrupted = false;
  }

  connect = (conversationId, onEvent) => {
    this.conversationId = conversationId;
    this.onEvent = onEvent;
    this.disconnected = false;
    return () => {
      this.disconnected = true;
      this.onEvent = null;
    };
  };

  send = clientEvent => {
    if (this.disconnected || !clientEvent) return;
    this.interrupted = false;
    this.process(clientEvent).catch(err => {
      if (err instanceof MockInterrupted) return;
      this.emit(AGENT_EVENTS.AGENT_ERROR, {
        message: err?.message || "Something went wrong in the demo agent."
      });
    });
  };

  // Demo parity with the live transport: stops the current turn's text
  // stream at the next token boundary instead of running it to completion.
  interrupt = () => {
    this.interrupted = true;
  };

  process = async clientEvent => {
    const step = this.steps.find(
      item => !this.consumed[item.id] && item.expects(clientEvent, this.ctx)
    );
    if (step) {
      this.consumed[step.id] = true;
      const result = await step.run(this.api(), clientEvent, this.ctx);
      if (result === false) {
        this.consumed[step.id] = false;
      }
      return;
    }
    if (clientEvent.type === CLIENT_EVENTS.USER_MESSAGE) {
      await this.fallbackTurn();
    }
  };

  fallbackTurn = async () => {
    const turn = this.beginTurn();
    await turn.text(
      "I'm running in demo mode right now, so I can only walk through the prospecting flow. Try asking for **companies in HR tech and SaaS with a funding round of $100M+**, point out a wrong title, or ask me to export to your CRM."
    );
    turn.end("user_input");
  };

  emit = (type, payload = {}) => {
    if (this.disconnected || !this.onEvent) return;
    this.ts += 1;
    this.onEvent({ type, ts: this.ts, ...payload });
  };

  api = () => ({
    wait,
    beginTurn: this.beginTurn,
    ctx: this.ctx
  });

  beginTurn = () => {
    const turnId = `t_${(this.turnSeq += 1)}`;
    this.emit(AGENT_EVENTS.TURN_START, { turnId });

    const openBlock = (type, payload = {}) => {
      const block = { id: `b_${(this.blockSeq += 1)}`, type, ...payload };
      this.emit(AGENT_EVENTS.BLOCK_START, { turnId, block });
      return block.id;
    };

    const closeBlock = blockId =>
      this.emit(AGENT_EVENTS.BLOCK_END, { turnId, blockId });

    const patch = (blockId, blockPatch) =>
      this.emit(AGENT_EVENTS.BLOCK_UPDATE, { turnId, blockId, patch: blockPatch });

    return {
      id: turnId,
      openBlock,
      closeBlock,
      patch,
      addBlock: (type, payload = {}) => {
        const blockId = openBlock(type, payload);
        closeBlock(blockId);
        return blockId;
      },
      // Streams markdown word-by-word with a small deterministic jitter so the
      // typing feel matches a real token stream.
      text: async markdown => {
        const blockId = openBlock("text", { markdown: "" });
        const tokens = (markdown || "").split(/(\s+)/).filter(Boolean);
        let buffer = "";
        for (let i = 0; i < tokens.length; i += 1) {
          if (this.interrupted) {
            closeBlock(blockId);
            this.emit(AGENT_EVENTS.TURN_END, { turnId, await: "user_input", hint: null });
            throw new MockInterrupted();
          }
          buffer += tokens[i];
          const flush = buffer.length > 6 || i === tokens.length - 1;
          if (flush) {
            this.emit(AGENT_EVENTS.BLOCK_DELTA, { turnId, blockId, delta: buffer });
            buffer = "";
            await wait(20 + ((i % 3) * 8));
          }
        }
        closeBlock(blockId);
        return blockId;
      },
      end: (awaitState = null, hint = null) =>
        this.emit(AGENT_EVENTS.TURN_END, { turnId, await: awaitState, hint })
    };
  };
}

export default MockAgentTransport;
